import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { setAuthToken } from "../lib/api";
import { toast } from "sonner";
import { useBrand } from "./BrandContext";

const STAFF_ROLES = ["usine", "transporteur", "admin"];

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const defaultSauce = useBrand().sauces[0] || "";
  const [role, setRole] = useState(() => localStorage.getItem("appro_role") || "client");
  const [clientId, setClientId] = useState(() => localStorage.getItem("appro_client") || null);
  const [cart, setCart] = useState({}); // productId -> {product, quantity}
  const [staffToken, setStaffToken] = useState(() => localStorage.getItem("appro_staff") || null);
  const [enseigneToken, setEnseigneToken] = useState(() => localStorage.getItem("appro_ens_token") || null);
  const [enseigneName, setEnseigneName] = useState(() => localStorage.getItem("appro_enseigne") || null);

  // Attach the right token to axios synchronously (before children fetch)
  setAuthToken(STAFF_ROLES.includes(role) ? staffToken : enseigneToken);

  // Register 401 handler synchronously during render so it is active before
  // any child component fires its mount-time API calls.
  const interceptorRef = useRef(false);
  if (!interceptorRef.current) {
    interceptorRef.current = true;
    api.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 401) {
          const hadSession = !!(localStorage.getItem("appro_staff") || localStorage.getItem("appro_ens_token"));
          setStaffToken(null);
          setEnseigneToken(null);
          setEnseigneName(null);
          setClientId(null);
          if (hadSession) toast.error("Session expirée, veuillez vous reconnecter.");
        }
        return Promise.reject(error);
      }
    );
  }

  useEffect(() => localStorage.setItem("appro_role", role), [role]);
  useEffect(() => {
    if (clientId) localStorage.setItem("appro_client", clientId);
    else localStorage.removeItem("appro_client");
  }, [clientId]);
  useEffect(() => {
    if (staffToken) localStorage.setItem("appro_staff", staffToken);
    else localStorage.removeItem("appro_staff");
  }, [staffToken]);
  useEffect(() => {
    if (enseigneToken) localStorage.setItem("appro_ens_token", enseigneToken);
    else localStorage.removeItem("appro_ens_token");
  }, [enseigneToken]);
  useEffect(() => {
    if (enseigneName) localStorage.setItem("appro_enseigne", enseigneName);
    else localStorage.removeItem("appro_enseigne");
  }, [enseigneName]);

  const loginStaff = useCallback(async (code) => {
    const { data } = await api.post("/auth/staff", { code });
    setStaffToken(data.token);
  }, []);
  const logoutStaff = useCallback(() => setStaffToken(null), []);
  const loginEnseigne = useCallback(async (enseigne, pin) => {
    const { data } = await api.post("/auth/enseigne", { enseigne, pin });
    setEnseigneToken(data.token);
    setEnseigneName(data.enseigne);
  }, []);
  const logoutEnseigne = useCallback(() => {
    setEnseigneToken(null);
    setEnseigneName(null);
    setClientId(null);
  }, []);

  const addToCart = useCallback((product, quantity = 1, sauce) => {
    setCart((c) => {
      const existing = c[product.id];
      const q = (existing ? existing.quantity : 0) + quantity;
      if (q <= 0) {
        const { [product.id]: _, ...rest } = c;
        return rest;
      }
      return { ...c, [product.id]: { product, quantity: q, sauce: sauce || existing?.sauce || defaultSauce } };
    });
  }, [defaultSauce]);

  const setQuantity = useCallback((product, quantity) => {
    setCart((c) => {
      if (quantity <= 0) {
        const { [product.id]: _, ...rest } = c;
        return rest;
      }
      const existing = c[product.id];
      return { ...c, [product.id]: { product, quantity, sauce: existing?.sauce || defaultSauce } };
    });
  }, [defaultSauce]);

  const setSauce = useCallback((product, sauce) => {
    setCart((c) => {
      const existing = c[product.id];
      if (!existing) return c;
      return { ...c, [product.id]: { ...existing, sauce } };
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((c) => {
      const { [productId]: _, ...rest } = c;
      return rest;
    });
  }, []);

  const clearCart = useCallback(() => setCart({}), []);

  const cartCount = Object.values(cart).reduce((s, i) => s + i.quantity, 0);
  const cartTotal = Object.values(cart).reduce((s, i) => s + i.quantity * i.product.price, 0);

  return (
    <AppContext.Provider
      value={{
        role, setRole,
        clientId, setClientId,
        cart, addToCart, setQuantity, setSauce, removeFromCart, clearCart,
        cartCount, cartTotal,
        staffToken, loginStaff, logoutStaff,
        enseigneName, loginEnseigne, logoutEnseigne,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
